import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Locate, Truck, Building2, Map as MapIcon, Store, Users, Star, Clock, Package } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 1 }).format(n || 0);
}

const TYPE_META = {
    supplier_hub: { label: "Supplier hub", icon: Building2, chip: "bg-cyan-100 text-cyan-700" },
    agent_point: { label: "Agent point", icon: Users, chip: "bg-amber-100 text-amber-800" },
    merchant_pickup: { label: "Merchant pickup", icon: Store, chip: "bg-emerald-100 text-emerald-700" },
};

export default function NearbyTab() {
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [needsLocation, setNeedsLocation] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hostBusy, setHostBusy] = useState(false);
    const [manual, setManual] = useState({ lat: "", lng: "", region: "urban", address_label: "" });
    const [typeFilter, setTypeFilter] = useState("all");
    const [hostConfigOpen, setHostConfigOpen] = useState(false);
    const [hostCfg, setHostCfg] = useState({ capacity: 50, hours: "08:00\u201318:00" });
    const [rateOpen, setRateOpen] = useState(null); // host id being rated
    const [ratingValue, setRatingValue] = useState(5);
    const [ratingReview, setRatingReview] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/locations/nearby?radius_km=500");
            setData(data);
            setNeedsLocation(false);
        } catch (err) {
            if (err?.response?.status === 400) setNeedsLocation(true);
            else toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    useEffect(() => { load(); }, []);

    const useBrowserLocation = () => {
        if (!navigator.geolocation) {
            toast.error("Geolocation is not supported on this device.");
            return;
        }
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    await api.post("/me/geo", {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        region: manual.region,
                        address_label: manual.address_label || null,
                    });
                    toast.success("Location updated");
                    await load();
                } catch (err) {
                    toast.error(formatApiError(err?.response?.data?.detail) || err.message);
                } finally {
                    setLoading(false);
                }
            },
            (err) => { setLoading(false); toast.error(err.message); },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const saveManual = async () => {
        if (!manual.lat || !manual.lng) {
            toast.error("Latitude and longitude required");
            return;
        }
        setLoading(true);
        try {
            await api.post("/me/geo", {
                lat: Number(manual.lat),
                lng: Number(manual.lng),
                region: manual.region,
                address_label: manual.address_label || null,
            });
            toast.success("Location saved");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const togglePickupHost = async () => {
        if (!data) return;
        // When enabling, open the host-config dialog first so we collect capacity + hours
        if (!data.pickup_host) {
            setHostCfg({
                capacity: data.pickup_capacity || 50,
                hours: data.pickup_hours || "08:00\u201318:00",
            });
            setHostConfigOpen(true);
            return;
        }
        setHostBusy(true);
        try {
            await api.post("/me/pickup-host", { enabled: false });
            toast.success("Pickup host disabled.");
            await load();
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setHostBusy(false);
        }
    };

    const submitHostConfig = async () => {
        setHostBusy(true);
        try {
            await api.post("/me/pickup-host", {
                enabled: true,
                capacity: Number(hostCfg.capacity),
                hours: hostCfg.hours,
            });
            toast.success("Application submitted. An admin will review your kiosk.");
            setHostConfigOpen(false);
            await load();
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setHostBusy(false);
        }
    };

    const submitRating = async () => {
        if (!rateOpen) return;
        try {
            await api.post(`/locations/hosts/${rateOpen}/rate`, {
                rating: Number(ratingValue),
                review: ratingReview || null,
            });
            toast.success("Thanks for rating this hub");
            setRateOpen(null);
            setRatingReview("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    const filteredPickups = (data?.pickups || []).filter((p) =>
        typeFilter === "all" ? true : p.type === typeFilter
    );

    return (
        <div className="space-y-6" data-testid="nearby-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-3xl font-extrabold text-onego-navy">Nearby network</h2>
                    <p className="text-slate-600">
                        Pickup / drop-off points are auto-generated from geotagged platform participants &mdash;
                        suppliers, super-agents, and merchants who opt in to host.
                    </p>
                </div>
                {user?.role === "merchant" && data && (
                    <button
                        onClick={togglePickupHost}
                        disabled={hostBusy}
                        className={`${data.pickup_host ? "og-btn-primary" : "og-btn-gradient"} inline-flex items-center gap-2 disabled:opacity-60`}
                        data-testid="toggle-pickup-host-btn"
                    >
                        <Store size={16} />
                        {hostBusy ? "Saving\u2026" : data.pickup_host ? "Stop hosting pickups" : "Become a pickup host"}
                    </button>
                )}
            </div>

            <div className="og-card p-6" data-testid="geo-controls">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="grow min-w-[180px]">
                        <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Latitude</label>
                        <input className="og-input mt-1" value={manual.lat} onChange={(e) => setManual({ ...manual, lat: e.target.value })} placeholder="-1.2921" data-testid="geo-lat-input" />
                    </div>
                    <div className="grow min-w-[180px]">
                        <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Longitude</label>
                        <input className="og-input mt-1" value={manual.lng} onChange={(e) => setManual({ ...manual, lng: e.target.value })} placeholder="36.8219" data-testid="geo-lng-input" />
                    </div>
                    <div className="min-w-[140px]">
                        <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Region</label>
                        <select className="og-input mt-1" value={manual.region} onChange={(e) => setManual({ ...manual, region: e.target.value })} data-testid="geo-region-select">
                            <option value="urban">Urban</option>
                            <option value="rural">Rural</option>
                        </select>
                    </div>
                    <div className="grow min-w-[220px]">
                        <label className="text-xs uppercase tracking-widest font-semibold text-slate-500">Address label</label>
                        <input className="og-input mt-1" value={manual.address_label} onChange={(e) => setManual({ ...manual, address_label: e.target.value })} placeholder="Stall 14, Westlands" data-testid="geo-address-input" />
                    </div>
                    <button onClick={saveManual} disabled={loading} className="og-btn-primary disabled:opacity-60" data-testid="geo-save-btn">Save</button>
                    <button onClick={useBrowserLocation} disabled={loading} className="og-btn-gradient inline-flex items-center gap-2 disabled:opacity-60" data-testid="geo-detect-btn">
                        <Locate size={16} /> Use my location
                    </button>
                </div>
                {data?.origin && (
                    <p className="mt-3 text-xs text-slate-500" data-testid="geo-origin">
                        Current: {data.origin.address_label || "—"} &middot; ({data.origin.lat?.toFixed(4)}, {data.origin.lng?.toFixed(4)}) &middot; {data.origin.region}
                        {data.pickup_host && data.pickup_status === "approved" && <span className="ml-2 og-chip bg-emerald-100 text-emerald-700">Pickup host • approved</span>}
                        {data.pickup_host && data.pickup_status === "pending" && <span className="ml-2 og-chip bg-amber-100 text-amber-800">Application pending</span>}
                        {data.pickup_host && data.pickup_status === "rejected" && <span className="ml-2 og-chip bg-rose-100 text-rose-700">Application rejected</span>}
                    </p>
                )}
            </div>

            {data?.pickup_host && data?.pickup_status === "pending" && (
                <div className="og-card p-6 bg-amber-50 border-amber-100 text-amber-900" data-testid="host-pending-banner">
                    <p className="font-display text-lg font-bold">Your pickup host application is pending review</p>
                    <p className="text-sm mt-1">An admin will approve your kiosk shortly. Once approved, it will appear in the network and other merchants can route orders to you.</p>
                </div>
            )}

            {needsLocation && (
                <div className="og-card p-6 bg-amber-50 border-amber-100 text-amber-900" data-testid="needs-location-banner">
                    <p className="font-display text-lg font-bold">Set your location to see nearby network</p>
                    <p className="text-sm mt-1">Use &ldquo;Use my location&rdquo; above, or paste coordinates manually.</p>
                </div>
            )}

            {data && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="og-card p-6">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <h3 className="font-display text-xl font-bold text-onego-navy flex items-center gap-2"><Truck size={18} /> Pickup &amp; drop-off network</h3>
                            <span className="og-chip">{filteredPickups.length} live</span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="pickup-filters">
                            {[
                                { v: "all", label: "All" },
                                { v: "supplier_hub", label: "Supplier hubs" },
                                { v: "agent_point", label: "Agent points" },
                                { v: "merchant_pickup", label: "Merchant pickups" },
                            ].map((f) => (
                                <button
                                    key={f.v}
                                    onClick={() => setTypeFilter(f.v)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${typeFilter === f.v ? "bg-onego-navy text-white border-onego-navy" : "border-slate-200 text-slate-600 hover:border-onego-navy"}`}
                                    data-testid={`pickup-filter-${f.v}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-3 divide-y divide-slate-100 max-h-[440px] overflow-auto">
                            {filteredPickups.length === 0 && <p className="py-4 text-sm text-slate-500">No pickup points in range yet.</p>}
                            {filteredPickups.map((p) => {
                                const meta = TYPE_META[p.type] || TYPE_META.supplier_hub;
                                const Icon = meta.icon;
                                return (
                                    <div key={p.id} className="py-3 flex items-start justify-between gap-3" data-testid={`pickup-row-${p.id}`}>
                                        <div className="min-w-0 flex items-start gap-3">
                                            <div className="h-9 w-9 rounded-2xl bg-slate-100 flex items-center justify-center text-onego-navy shrink-0">
                                                <Icon size={16} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-bold text-onego-navy truncate">{p.name}</p>
                                                <p className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                                                    <MapPin size={11} /> {p.city || p.country || "—"}
                                                    <span className={`og-chip ${meta.chip} ml-1`}>{meta.label}</span>
                                                    <span className="og-chip ml-1 capitalize">{p.region}</span>
                                                </p>
                                                <p className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                                                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.hours}</span>
                                                    <span className="inline-flex items-center gap-1"><Package size={11} /> cap {p.capacity}/day</span>
                                                    {p.rating > 0 ? (
                                                        <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><Star size={11} fill="currentColor" /> {p.rating} ({p.review_count})</span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-slate-400">Unrated</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end gap-2 shrink-0">
                                            <span className="og-chip og-gradient text-white">{fmt(p.distance_km)} km</span>
                                            <button
                                                onClick={() => { setRateOpen(p.id); setRatingValue(5); setRatingReview(""); }}
                                                className="text-xs font-bold text-pink-600 hover:underline inline-flex items-center gap-1"
                                                data-testid={`rate-host-${p.id}`}
                                            >
                                                <Star size={11} /> Rate
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="og-card p-6">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <h3 className="font-display text-xl font-bold text-onego-navy flex items-center gap-2"><Building2 size={18} /> Nearest suppliers</h3>
                            <span className="og-chip">{data.suppliers?.length || 0}</span>
                        </div>
                        <div className="mt-3 divide-y divide-slate-100 max-h-[440px] overflow-auto">
                            {(!data.suppliers || data.suppliers.length === 0) && <p className="py-4 text-sm text-slate-500">No suppliers in range.</p>}
                            {(data.suppliers || []).map((s) => (
                                <div key={s.id} className="py-3 flex items-center justify-between gap-3" data-testid={`supplier-row-${s.id}`}>
                                    <div className="min-w-0">
                                        <p className="font-bold text-onego-navy truncate">{s.name}</p>
                                        <p className="text-xs text-slate-500 flex items-center gap-1"><MapIcon size={12} /> {s.address || s.country}</p>
                                    </div>
                                    <span className="og-chip">{fmt(s.distance_km)} km</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Host config dialog (merchants applying to be a pickup host) */}
            <Dialog open={hostConfigOpen} onOpenChange={setHostConfigOpen}>
                <DialogContent data-testid="host-config-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Apply to be a pickup host</DialogTitle>
                        <DialogDescription>Tell us how much capacity you can offer and your opening hours. An admin will review and approve.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Daily parcel capacity</label>
                            <input className="og-input mt-1" type="number" min={1} max={5000} value={hostCfg.capacity} onChange={(e) => setHostCfg({ ...hostCfg, capacity: e.target.value })} data-testid="host-capacity-input" />
                            <p className="text-xs text-slate-500 mt-1">How many parcels can you safely store + hand over each day?</p>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Opening hours</label>
                            <input className="og-input mt-1" value={hostCfg.hours} onChange={(e) => setHostCfg({ ...hostCfg, hours: e.target.value })} placeholder="08:00\u201318:00" data-testid="host-hours-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setHostConfigOpen(false)} className="og-btn-ghost" data-testid="host-config-cancel">Cancel</button>
                        <button onClick={submitHostConfig} disabled={hostBusy} className="og-btn-gradient disabled:opacity-60" data-testid="host-config-submit">
                            {hostBusy ? "Submitting\u2026" : "Submit application"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Rate hub dialog */}
            <Dialog open={!!rateOpen} onOpenChange={(o) => !o && setRateOpen(null)}>
                <DialogContent data-testid="rate-host-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Rate this pickup hub</DialogTitle>
                        <DialogDescription>Share your experience so other merchants can find the best hubs.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="flex items-center gap-1" data-testid="rate-stars">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setRatingValue(s)}
                                    className="p-1"
                                    data-testid={`star-${s}`}
                                >
                                    <Star size={28} className={s <= ratingValue ? "text-amber-500" : "text-slate-300"} fill={s <= ratingValue ? "currentColor" : "none"} />
                                </button>
                            ))}
                        </div>
                        <textarea className="og-input" rows={3} value={ratingReview} onChange={(e) => setRatingReview(e.target.value)} placeholder="Optional review" data-testid="rate-review-input" />
                    </div>
                    <DialogFooter>
                        <button onClick={() => setRateOpen(null)} className="og-btn-ghost" data-testid="rate-cancel">Cancel</button>
                        <button onClick={submitRating} className="og-btn-gradient" data-testid="rate-submit">Submit rating</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
